import { CurrentUser, LiteModeKeys, UserState, VideoQuality, getDefaultState } from './types';

import type { AgendaFavorite, User } from '../event/types';

import analytics from '@/analytics';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { ChatEvents } from '@/libs/eventBus/chatEvents';
import EventBus from '@/libs/eventBus/eventBus';
import { saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { getUserProfileImageUrl, getPublicThumbnailImage, getMediumSizedImage } from '@/services/user_image.service';
import { RootState } from '@/store';

const mutations = {
  setCurrentUser(state: UserState, user: CurrentUser) {
    state.currentUser = user;
  },
  setCognitoUserDetails(state: UserState, cognitoUserDetails: any) {
    state.cognitoUserDetails = cognitoUserDetails;
  },
  setCurrentUserSession(state: UserState, session: { sessionID: string }) {
    if (state.currentUser) {
      state.currentUser.sessionID = session.sessionID;
    }
  },
  setSelectedVideoQuality(state: UserState, quality: VideoQuality) {
    state.selected_video_quality = quality;
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.selectedVideoQuality, quality);
  },
  setPrivateDiscussionRoom(state: UserState, privateDiscussionRoom: { id: string }) {
    state.privateDiscussionRoom = privateDiscussionRoom;
  },
  cleanState(state: UserState) {
    Object.assign(state, getDefaultState());
  },
  closeLiteModePrompts(state: UserState, supress_dialog?: boolean) {
    const currentTimeStamp = Date.now();
    state.liteMode.liteModeState = 'LOADED';
    state.liteMode.supressedLiteModeConfirmation = supress_dialog || state.liteMode.supressedLiteModeConfirmation;
    state.liteMode.lastLiteModeSwitchPromptTimestamp = currentTimeStamp;
    state.liteMode.lastLiveModeSwitchPromptTimestamp = currentTimeStamp;
    saveValueToLocalstore(LiteModeKeys.supressedLiteModeStoreKey, state.liteMode.supressedLiteModeConfirmation);
  },
  delayedLiteModeStateSwitchToLoaded(state: UserState) {
    setTimeout(() => {
      state.liteMode.liteModeState = 'LOADED';
    }, 3000);
  },
  checkAndPromptLiveModeSwitch(state: UserState, rootState: RootState) {
    const isCurrentRouteBroadcast = rootState.route?.fullPath?.includes('/broadcast');
    if (!isCurrentRouteBroadcast) {
      return;
    }
    if (
      state.liteMode.liteModeActive &&
      !state.liteMode.supressedLiteModeConfirmation &&
      state.liteMode.liteModeState !== 'PROMPTLIVEMODESWITCH'
    ) {
      state.liteMode.liteModeState = 'PROMPTLIVEMODESWITCH';
      analytics.track('lite_mode_off_prompt');
    } else if (state.liteMode.liteModeState === 'PROMPTLITEMODESWITCH') {
      state.liteMode.liteModeState = 'LOADED';
    }
  },
  browserSupportsHlsPlayback(state: UserState, support: boolean) {
    state.hlsPlaybackSupported = support;
  },
  recordInterestInLiteMode(state: UserState) {
    state.liteMode.willShowLiveModeAvailablePrompt = true;
  },
  checkAndShowLiteModeAvalablePrompt(state: UserState) {
    if (state.liteMode.willShowLiveModeAvailablePrompt) {
      state.liteMode.willShowLiveModeAvailablePrompt = false;
      state.liteMode.liteModeState = 'AVAILABLEAGAINPROMPT';
      state.liteMode.liteModeAvailablityPromptTimeout = setTimeout(() => {
        state.liteMode.liteModeState = 'LOADED';
      }, 3000);
    }
  },
  setCurrentEventUser(state: UserState, user: User) {
    state.currentEventUser = user;
    window.eventuserid = user?.eventUser?.id ?? 'notset';
  },
  updateCurrentUser(state: UserState, user: User) {
    state.currentUser = { ...state.currentUser, ...user };
  },
  optimisticallyUpdateCurrentUser(state: UserState, payload: { user: any; rootState: any }) {
    const user = payload.user;
    const rootState = payload.rootState;
    const possibleUpdates = [
      'first_name',
      'last_name',
      'title',
      'company',
      'location',
      'linkedin_url',
      'twitter_url',
      'tags',
      'profile_picture_url',
      'event'
    ];
    let updateName = false;
    let updatePicture = false;
    for (let i = 0; i < possibleUpdates.length; i++) {
      const key = possibleUpdates[i];
      if (!user[key]) {
        continue;
      }
      if (
        (state.currentEventUser && user[key] !== (state.currentEventUser as any)[key]) ||
        (state.currentUser && user[key] !== (state.currentUser as any)[key])
      ) {
        if (state.currentEventUser) {
          (state.currentEventUser as any)[key] = user[key];
        }
        if (state.currentUser) {
          (state.currentUser as any)[key] = user[key];
        }
        if (key === 'first_name' || key === 'last_name') {
          updateName = true;
        }

        if (key === 'profile_picture_url') {
          updatePicture = true;
        }
      }
    }

    const userCopiesToUpdate: Array<User> = [];
    if (state.currentEventUser) {
      userCopiesToUpdate.push(state.currentEventUser);
    }
    if (state.currentUser) {
      userCopiesToUpdate.push(state.currentUser);
    }

    for (let bcastIndex = 0; bcastIndex < rootState.event.event?.agenda?.broadcasts?.length || 0; ++bcastIndex) {
      const bcast = (rootState.event.event?.agenda?.broadcasts ?? [])[bcastIndex];
      if (bcast) {
        for (let speakerIndex = 0; speakerIndex < bcast.speakers.length; ++speakerIndex) {
          const speaker = bcast.speakers[speakerIndex];
          if (state.currentUser && speaker.id == state.currentUser.id) {
            userCopiesToUpdate.push(speaker);
          } else {
            loggerService.info('Optimisitic update for speaker failed: ', state.currentUser);
          }
        }
      }
    }

    const allUsers = rootState.event.event?.users;

    if (allUsers && state.currentUser && allUsers[state.currentUser.id]) {
      userCopiesToUpdate.push(allUsers[state.currentUser.id]);
    } else if (allUsers && state.currentUser && allUsers[state.currentUser.mmid]) {
      userCopiesToUpdate.push(allUsers[state.currentUser.mmid]);
    } else {
      loggerService.info('Optimistic update failure: ', allUsers, state.currentUser);
    }

    for (let userRefIndex = 0; userRefIndex < userCopiesToUpdate.length; ++userRefIndex) {
      const ref = userCopiesToUpdate[userRefIndex] as any;
      if (updateName) {
        user.first_name = user.first_name || ref.first_name;
        user.last_name = user.last_name || ref.last_name;
        if (ref?.name) {
          ref.name = user.first_name + ' ' + user.last_name;
        }
      }

      if (updatePicture) {
        ref.image = getUserProfileImageUrl(user);
        ref.thumbnail = getPublicThumbnailImage(user);
        ref.mediumimage = getMediumSizedImage(user);
      } else if (user.profile_picture_url !== undefined && !user.profile_picture_url) {
        ref.profile_picture_url = '';
        ref.image = '';
        ref.thumbnail = '';
        ref.mediumimage = '';
      }
    }
  },
  updateCurrentEventUser(state: UserState, user: User) {
    state.currentEventUser = { ...state.currentEventUser, ...user };
  },
  appendToTechCheckedEvents(state: UserState, eventId: string) {
    const updatedArray = [...state.techCheckedEvents, eventId];
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.techCheckedEvents, updatedArray);
    state.techCheckedEvents = updatedArray;
  },
  setWatchLive(state: UserState, payload: { id: string; setTo: boolean }) {
    if (state.isWatchingLive[payload.id] !== undefined) {
      state.isWatchingLive[payload.id] = payload.setTo;
    } else {
      state.isWatchingLive = {
        ...state.isWatchingLive,
        [payload.id]: payload.setTo
      };
    }
  },
  removeAgendaFavorite(state: UserState, agendaFavorite: AgendaFavorite) {
    if (!state.currentEventUser || !state.currentEventUser.status || !state.currentEventUser.status.agendaFavorites) {
      return;
    }

    const filteredFavorites = state.currentEventUser.status.agendaFavorites.filter(e => {
      if (e.agenda_item && e.agenda_item !== agendaFavorite.agenda_item) {
        return true;
      }

      if (e.broadcast && e.broadcast !== agendaFavorite.broadcast) {
        return true;
      }

      return false;
    });

    state.currentEventUser.status.agendaFavorites = filteredFavorites;
  },
  addAgendaFavorite(state: UserState, agendaFavorite: AgendaFavorite) {
    if (!state.currentEventUser?.status) {
      return;
    }

    if (!state.currentEventUser.status.agendaFavorites) {
      state.currentEventUser.status.agendaFavorites = [agendaFavorite];
    } else {
      state.currentEventUser.status.agendaFavorites.push(agendaFavorite);
    }
  },
  updateAgendaFavorite(state: UserState, updatedFavorite: AgendaFavorite) {
    const agendaFavorites = state.currentEventUser?.status?.agendaFavorites;

    if (!agendaFavorites) {
      return;
    }

    const existingIndex = agendaFavorites.findIndex(e => {
      if (e.agenda_item && e.agenda_item === updatedFavorite.agenda_item) {
        return true;
      }

      if (e.broadcast && e.broadcast === updatedFavorite.broadcast) {
        return true;
      }

      return false;
    });

    agendaFavorites[existingIndex] = updatedFavorite;
  },
  setPermissions(state: UserState, permissions: UserState['permissions']) {
    state.permissions = permissions;
  },
  setAccessToken(state: UserState, accessToken: string) {
    state.accessToken = accessToken;
  },
  setRefreshToken(state: UserState, refreshToken: string) {
    state.refreshToken = refreshToken;
  }
};

export default mutations;
export type UserMutations = typeof mutations;
